/**
 * Shared note text for appointment_booked activity_logs (CRM BookAppointmentModal).
 * Edge Function book-agent-appointment mirrors this format inline (Deno cannot import src/).
 */

const APPOINTMENT_TYPE_LABELS = {
  intro_call: 'Intro Call',
  needs_analysis: 'Needs Analysis',
  policy_review: 'Policy Review',
  closing_call: 'Closing Call',
  discovery_call: 'Discovery Call',
  business_presentation: 'Business Presentation',
  follow_up: 'Follow-Up',
  onboarding: 'Onboarding',
  strategy_session: 'Strategy Session',
  proposal_review: 'Proposal Review',
  presentation: 'Presentation',
};

/**
 * Format scheduled_at in an IANA timezone as e.g. "Tue Oct 7, 2026 · 2:00 PM ET".
 */
export function formatAppointmentWhen(iso, timeZone = 'America/New_York') {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const tz = timeZone || 'America/New_York';
  try {
    const weekday = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short' }).format(date);
    const month = new Intl.DateTimeFormat('en-US', { timeZone: tz, month: 'short' }).format(date);
    const day = new Intl.DateTimeFormat('en-US', { timeZone: tz, day: 'numeric' }).format(date);
    const year = new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric' }).format(date);
    const time = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
    const tzName = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short',
    })
      .formatToParts(date)
      .find((p) => p.type === 'timeZoneName')?.value || '';
    const timePart = tzName ? `${time} ${tzName}` : time;
    return `${weekday} ${month} ${day}, ${year} · ${timePart}`;
  } catch {
    return null;
  }
}

/** Civil date (YYYY-MM-DD) of an instant in the given IANA timezone. */
export function activityDateFromScheduledAt(iso, timeZone = 'America/New_York') {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const tz = timeZone || 'America/New_York';
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const y = parts.find((p) => p.type === 'year')?.value;
    const m = parts.find((p) => p.type === 'month')?.value;
    const d = parts.find((p) => p.type === 'day')?.value;
    if (y && m && d) return `${y}-${m}-${d}`;
  } catch {
    /* fall through */
  }
  return null;
}

export function appointmentTypeLabel(type) {
  if (!type) return 'Appointment';
  return APPOINTMENT_TYPE_LABELS[type] || String(type).replace(/_/g, ' ');
}

export function meetingLine(meetingType, meetingLocation) {
  const loc = (meetingLocation || '').trim();
  if (meetingType === 'zoom') return 'Meeting: Zoom';
  if (meetingType === 'custom_link') {
    return loc ? `Meeting: Custom link — ${loc}` : 'Meeting: Custom link';
  }
  if (meetingType === 'in_person') {
    return loc ? `Meeting: In person — ${loc}` : 'Meeting: In person';
  }
  if (loc) return `Meeting: ${meetingType || 'Unknown'} — ${loc}`;
  return meetingType ? `Meeting: ${meetingType}` : null;
}

function formatGuest(guest) {
  if (!guest) return null;
  const name = (guest.name || '').trim() || 'Guest';
  const email = (guest.email || '').trim();
  return email ? `${name} (${email})` : name;
}

/**
 * @param {object} ctx
 * @param {string} ctx.title
 * @param {string} [ctx.appointmentType]
 * @param {string} ctx.scheduledAt ISO
 * @param {number} [ctx.durationMinutes]
 * @param {string} [ctx.timeZone]
 * @param {string} [ctx.meetingType]
 * @param {string|null} [ctx.meetingLocation]
 * @param {string|null} [ctx.closerName]
 * @param {Array<{name?: string, email?: string}>} [ctx.guests]
 * @param {string} ctx.bookedByName
 * @param {boolean} [ctx.viaPublicPage]
 */
export function buildAppointmentBookedNotes(ctx) {
  const timeZone = ctx.timeZone?.trim() || 'America/New_York';
  const when =
    formatAppointmentWhen(ctx.scheduledAt, timeZone) || ctx.scheduledAt || 'TBD';
  const dur = Number(ctx.durationMinutes) || 30;
  const lines = [
    `Appointment booked: ${ctx.title || 'Appointment'}`,
    `Type: ${appointmentTypeLabel(ctx.appointmentType)}`,
    `When: ${when} (${dur} min)`,
  ];

  const meet = meetingLine(ctx.meetingType, ctx.meetingLocation);
  if (meet) lines.push(meet);

  const closer = (ctx.closerName || '').trim();
  if (closer) lines.push(`Closer: ${closer}`);

  const guests = (ctx.guests || []).map(formatGuest).filter(Boolean);
  if (guests.length) lines.push(`Guests: ${guests.join(', ')}`);

  const bookedBy = (ctx.bookedByName || '').trim() || 'Unknown';
  if (ctx.viaPublicPage) {
    lines.push(`Booked by: ${bookedBy} via public booking page`);
  } else {
    lines.push(`Booked by: ${bookedBy}`);
  }

  return lines.join('\n');
}
